import { TimeModel } from '../models/time.model';
import { timeFormatsEnum } from '../../../Core/Users/enums/time-formats.enum';
import { meridiansEnum } from '../enums/meridians.enum';
import { TwelveHourModel } from '../models/twelve-hour.model';
import { TwentyFourHourModel } from '../models/twenty-four-hour.model';
import { MinuteModel } from '../models/minute.model';
import moment from 'moment-timezone';
import 'moment-timezone';
import { Injectable } from '@angular/core';
import { SecondModel } from '../models/second.model';
import { timeScalesEnum } from '../../../Monitor/Shared/enums/timeScales.enum';
import { ChartTimeIntervalsEnum } from '../../../Common/Reports/enums/chart-time-intervals.enum';
import { DateGranularityModel } from '../../../Common/Charts/models/date-granularity.model';


export const timezoneConversionDateFormat = 'ddd MMM DD YYYY HH:mm';

@Injectable()
export class TimeHelper {

    /**
     * Converts a timestamp of any number of digits to a 13 digit timestamp
     */
    public static convertTo13DigitTimestamp(timestamp: number): number {
        if (!timestamp) {
            return null;
        }
        const size = timestamp.toString().length;
        const desiredDigits = 13;
        let factor = 1;

        if (size > desiredDigits) {
            factor = 0.1 ** (size - desiredDigits);
        } else if (size < desiredDigits) {
            factor = 10 ** (desiredDigits - size);
        }

        return Math.floor(timestamp * factor);
    }

    /**
    * Returns a new time object based on a provided time object in the requested format
    *
    * @deprecated Use timeModel.changeFormat instead.
    */
    changeFormat(time: TimeModel, newFormat: timeFormatsEnum): TimeModel {

        const oldFormat: timeFormatsEnum = time.format;
        const oldHour: TwelveHourModel | TwentyFourHourModel = time.hour || this.getDefaultHour(oldFormat);
        const oldMinute: MinuteModel = time.minute;
        const oldTime: TimeModel = time;

        let newHour: TwelveHourModel | TwentyFourHourModel;
        let newMeridian: meridiansEnum;

        // If there is nothing to change or
        if (newFormat === oldFormat) {

            return oldTime;

        }

        // Otherwise, change the format
        if (newFormat === timeFormatsEnum.twentyFourHour) {

            if (time.meridian === meridiansEnum.AM) {

                // If it's midnight, the hour should be zero
                if (oldHour === 12) {

                    newHour = 0;

                } else {

                    newHour = oldHour;

                }

            } else if (time.meridian === meridiansEnum.PM) {

                // If it's noon, the hour should be 12
                if (oldHour === 12) {

                    newHour = oldHour;

                } else {

                    newHour = <TwentyFourHourModel>(oldHour + 12);

                }

            }

            newMeridian = null;

        } else if (newFormat === timeFormatsEnum.twelveHour) {

            if (oldHour < 12) {

                newMeridian = meridiansEnum.AM;

            } else {

                newMeridian = meridiansEnum.PM;

            }

            // If it's midnight, the hour should be twelve
            if (oldHour === 0) {

                newHour = 12;

            } else if (oldHour <= 12) {

                newHour = oldHour;

            } else if (oldHour > 12) {

                newHour = <TwelveHourModel>(oldHour - 12);

            }

        }

        const newTime: TimeModel = new TimeModel(
            newFormat,
            newHour,
            oldMinute,
            newMeridian,
            time.timeZone
        );

        return newTime;
    }


    /**
   * Creates a time object based on the provided date, timezone, and format
   */
    getTimeFromDate(date: Date, timezone: string, format: timeFormatsEnum): TimeModel {

        let time: TimeModel = null;
        const momentDate: moment.Moment = moment(date);

        // Adjust the date based on the timezone
        momentDate.tz(timezone);

        // Moment only operates in 24 hour time, so create a time object in 24 hour time
        const twentyFourHourTime: TimeModel = new TimeModel(
            timeFormatsEnum.twentyFourHour,
        <TwentyFourHourModel>momentDate.hour(),
        <MinuteModel>momentDate.minute(),
        null,
        timezone
        );

        // Convert 24 hour time to desired format
        time = this.changeFormat(twentyFourHourTime, format);

        return time;

    }

    /**
     * Creates a time object based on the provided date, timezone, and format
     */
    getMidnightToday(timezone: string, format: timeFormatsEnum): TimeModel {

        let time: TimeModel = null;
        const momentDate: moment.Moment = moment();

        // Adjust the date based on the timezone
        momentDate.tz(timezone);
        momentDate.startOf('day');

        // Moment only operates in 24 hour time, so create a time object in 24 hour time
        const twentyFourHourTime: TimeModel = new TimeModel(
            timeFormatsEnum.twentyFourHour,
            <TwentyFourHourModel>momentDate.hour(),
            <MinuteModel>momentDate.minute(),
            null,
            timezone
        );

        // Convert 24 hour time to desired format
        time = this.changeFormat(twentyFourHourTime, format);

        return time;

    }

    /**
   * Convert a number in seconds (duration) to a time object
   */
    conventDurationToTime(duration: number): TimeModel {
        const time = new TimeModel();
        if (duration !== null && duration !== undefined) {
            time.setMinute(<MinuteModel>Math.floor(duration / 60));
            time.setSecond(<SecondModel> (duration % 60));
        }
        return time;
    }

    /**
   * Convert a time object to a number in seconds (duration)
   */
    conventTimeToDuration(time: TimeModel): number {
        return time.hour * 3600 + time.minute * 60 + time.second;
    }

    /**
     * Converts a timestamp of any number of digits to a 13 digit timestamp
     */
    convertTo13DigitTimestamp(timestamp: number): number {
        return TimeHelper.convertTo13DigitTimestamp(timestamp);
    }

    /**
   * Get the default hour based on the provided time format
  */
    private getDefaultHour(format: timeFormatsEnum): TwelveHourModel | TwentyFourHourModel {

        let defaultHour: TwelveHourModel | TwentyFourHourModel = null;

        if (format === timeFormatsEnum.twelveHour) {
            defaultHour = 12;
        } else if (format === timeFormatsEnum.twentyFourHour) {
            defaultHour = 0;
        }

        return defaultHour;

    }

    public getDateGranularity(dateRangeInHours: number, timeInterval: ChartTimeIntervalsEnum): DateGranularityModel | undefined {
        if (timeInterval && timeInterval !== ChartTimeIntervalsEnum.DYNAMIC) {
            return timeIntervalsToDategranularitiesMap.get(timeInterval);
        }

        const hourAdjust = 2;
        const hours = dateRangeInHours ?? Infinity;
        if (hours <= 24 + hourAdjust) {
            return { timeUnit: 'hour', count: 1 };
        } else if (hours <= 24 * 36 + hourAdjust) {
            return { timeUnit: 'day', count: 1 };
        } else if (hours <= 24 * 7 * 36 + hourAdjust) {
            return { timeUnit: 'week', count: 1 };
        } else if (hours <= 24 * 30 * 36 + hourAdjust) {
            return { timeUnit: 'month', count: 1 };
        } else {
            return { timeUnit: 'month', count: 3 };
        }
    }
}

// checked coarsest-first; moment#add is calendar-aware (correct days-in-month, leap years, and DST transitions
// when adding days/weeks in local time), so this classifies a bucket by which calendar unit its own start,
// advanced by one of that unit, reaches its end - rather than comparing against a fixed millisecond duration
export function getTimeScale(from: number, to: number): moment.unitOfTime.StartOf {
    const start = moment(from);
    const end = moment(to);

    let scale: timeScalesEnum;
    if (start.clone().add(1, 'year').isSameOrBefore(end)) {
        scale = timeScalesEnum.yearly;
    } else if (start.clone().add(1, 'quarter').isSameOrBefore(end)) {
        scale = timeScalesEnum.quarterly;
    } else if (start.clone().add(1, 'month').isSameOrBefore(end)) {
        scale = timeScalesEnum.monthly;
    } else if (start.clone().add(1, 'week').isSameOrBefore(end)) {
        scale = timeScalesEnum.weekly;
    } else if (start.clone().add(1, 'day').isSameOrBefore(end)) {
        scale = timeScalesEnum.daily;
    } else {
        scale = timeScalesEnum.hourly;
    }

    let timeScale: moment.unitOfTime.StartOf;
    switch (scale) {
        case timeScalesEnum.hourly:
            timeScale = 'hour';
            break;
        case timeScalesEnum.daily:
            timeScale = 'day';
            break;
        case timeScalesEnum.weekly:
            timeScale = 'week';
            break;
        case timeScalesEnum.monthly:
            timeScale = 'month';
            break;
        case timeScalesEnum.quarterly:
            timeScale = 'quarter';
            break;
        case timeScalesEnum.yearly:
            timeScale = 'year';
            break;
        default:
            timeScale = null;
            break;
    }
    return timeScale;
}

export const timeIntervalsToDategranularitiesMap: Map<ChartTimeIntervalsEnum, DateGranularityModel> = new Map([
    [ChartTimeIntervalsEnum.WEEK, { timeUnit: 'week', count: 1 }],
    [ChartTimeIntervalsEnum.MONTH, { timeUnit: 'month', count: 1 }],
    [ChartTimeIntervalsEnum.QUARTER, { timeUnit: 'month', count: 3 }],
    [ChartTimeIntervalsEnum.YEAR, { timeUnit: 'year', count: 1}]
]);


